import { Link } from 'react-router-dom';
import { useDoors, useGroups, useLocations, useLocks, useMemberships, usePersonAppStatus, usePersons } from '../data/hooks';
import { onboardingComplete, onboardingSteps, type OnboardingStep } from '../lib/onboarding';
import { Panel } from './ui';

/** Stappen voor deze club, of null zolang de data laadt (of niet leesbaar is). */
export function useOnboarding(orgId: string): OnboardingStep[] | null {
  const memberships = useMemberships(orgId);
  const locks = useLocks(orgId);
  const doors = useDoors(orgId);
  const locations = useLocations(orgId);
  const groups = useGroups(orgId);
  const persons = usePersons(orgId);
  const appStatus = usePersonAppStatus(orgId);
  const qs = [memberships, locks, doors, locations, groups, persons, appStatus];
  if (qs.some((q) => !q.data)) return null;
  return onboardingSteps({
    memberships: memberships.data!,
    locks: locks.data!,
    doors: doors.data!,
    locationCount: locations.data!.length,
    groups: groups.data!,
    persons: persons.data!,
    appStatus: appStatus.data!,
  });
}

/**
 * "Zo zet je de club op": afgeleid uit de data, verdwijnt op het dashboard zodra alles klaar is.
 * `alwaysShow` voor het clubdetail van Antreja (daar blijft de stand zichtbaar).
 */
export function OnboardingChecklist({ orgId, alwaysShow = false }: { orgId: string; alwaysShow?: boolean }) {
  const steps = useOnboarding(orgId);
  if (!steps) return null;
  const complete = onboardingComplete(steps);
  if (complete && !alwaysShow) return null;
  const done = steps.filter((s) => s.done).length;
  return (
    <Panel
      title="Club opzetten"
      description={
        complete
          ? 'Alle stappen zijn klaar.'
          : `${done} van ${steps.length} stappen klaar.${alwaysShow ? '' : ' Deze lijst verdwijnt als alles klaar is.'}`
      }
    >
      <ol className="checklist">
        {steps.map((s) => (
          <li key={s.key} className={s.done ? 'is-done' : undefined}>
            <span className="check" aria-hidden="true">
              {s.done ? '✓' : ''}
            </span>
            <span>
              <span className="primary">
                {s.path && !s.done ? <Link to={`/c/${orgId}/${s.path}`}>{s.label}</Link> : s.label}
                <span className="sr-only">{s.done ? ' (klaar)' : ' (nog te doen)'}</span>
              </span>
              <span className="cell-sub">{s.detail}</span>
            </span>
          </li>
        ))}
      </ol>
    </Panel>
  );
}
